import { useMemo } from 'react';
import { Check, Calendar, Phone, Users, Bell, MoreHorizontal } from 'lucide-react';
import type { CalendarItem, CalendarEventType } from '../../types/calendar';
import {
  getWeekViewDays,
  WEEKDAY_LABELS_FULL,
  isToday,
  getItemsForDay,
  formatTime,
  CALENDAR_EVENT_TYPE_COLORS,
} from '../../types/calendar';

interface CalendarWeekViewProps {
  currentDate: Date;
  items: CalendarItem[];
  onDayClick: (date: Date) => void;
  onItemClick: (item: CalendarItem) => void;
  focusedEventId?: string | null;
}

const EVENT_TYPE_ICONS: Record<CalendarEventType, typeof Calendar> = {
  event: Calendar,
  call: Phone,
  meeting: Users,
  reminder: Bell,
  custom: MoreHorizontal,
};

export function CalendarWeekView({
  currentDate,
  items,
  onDayClick,
  onItemClick,
  focusedEventId,
}: CalendarWeekViewProps) {
  const days = useMemo(() => getWeekViewDays(currentDate, true), [currentDate]);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      {/* Days grid */}
      <div className="grid grid-cols-7 divide-x divide-gray-100 dark:divide-slate-700">
        {days.map((day, index) => {
          const dayItems = getItemsForDay(items, day);

          return (
            <div
              key={index}
              className="min-h-[400px] flex flex-col"
            >
              {/* Day header */}
              <div
                onClick={() => onDayClick(day)}
                className={`
                  p-3 text-center border-b border-gray-100 dark:border-slate-700 cursor-pointer
                  hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors
                  ${isToday(day) ? 'bg-primary/5' : ''}
                `}
              >
                <div className="text-xs text-[#6B6B6B] dark:text-slate-400 mb-1">
                  {WEEKDAY_LABELS_FULL[day.getDay()]}
                </div>
                <div
                  className={`
                    w-10 h-10 mx-auto flex items-center justify-center rounded-full text-lg font-semibold
                    ${isToday(day) ? 'bg-primary text-white' : 'text-[#1A1A1A] dark:text-white'}
                  `}
                >
                  {day.getDate()}
                </div>
              </div>

              {/* Items */}
              <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
                {dayItems.map((item) => (
                  <CalendarItemCard
                    key={item.id}
                    item={item}
                    onClick={() => onItemClick(item)}
                    isFocused={focusedEventId === item.id || focusedEventId === item.originalEventId}
                  />
                ))}
                {dayItems.length === 0 && (
                  <div className="text-center text-xs text-gray-400 dark:text-slate-500 py-4">
                    Keine Termine
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CalendarItemCardProps {
  item: CalendarItem;
  onClick: () => void;
  isFocused?: boolean;
}

function CalendarItemCard({ item, onClick, isFocused }: CalendarItemCardProps) {
  if (item.source === 'todo') {
    return (
      <button
        onClick={onClick}
        className={`
          w-full text-left p-2 rounded-lg text-sm transition-all
          ${isFocused
            ? 'ring-2 ring-primary ring-offset-2 shadow-lg'
            : ''
          }
          ${item.todoCompleted
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700'
            : 'bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700'
          }
        `}
      >
        <div className="flex items-start gap-2">
          <div
            className={`
              w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5
              ${item.todoCompleted ? 'bg-green-500 text-white' : 'border-2 border-violet-400 dark:border-violet-500'}
            `}
          >
            {item.todoCompleted && <Check className="w-3 h-3" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-medium truncate ${item.todoCompleted ? 'line-through text-gray-500 dark:text-slate-500' : 'text-[#1A1A1A] dark:text-white'}`}>
              {item.title}
            </p>
            <p className="text-xs text-[#6B6B6B] dark:text-slate-400">To-Do</p>
          </div>
        </div>
      </button>
    );
  }

  const colors = item.eventType
    ? CALENDAR_EVENT_TYPE_COLORS[item.eventType]
    : CALENDAR_EVENT_TYPE_COLORS.event;
  const Icon = item.eventType ? EVENT_TYPE_ICONS[item.eventType] : Calendar;

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left p-2 rounded-lg text-sm border transition-all
        ${isFocused
          ? 'ring-2 ring-primary ring-offset-2 shadow-lg'
          : ''
        }
        ${colors.bg} ${colors.border}
      `}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.text}`} />
        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${colors.text}`}>
            {item.title}
          </p>
          {!item.allDay && (
            <p className="text-xs opacity-75">
              {formatTime(item.start)} - {formatTime(item.end)}
            </p>
          )}
          {item.allDay && (
            <p className="text-xs opacity-75">Ganztägig</p>
          )}
          {item.location && (
            <p className="text-xs opacity-75 truncate">{item.location}</p>
          )}
        </div>
      </div>
    </button>
  );
}
